import type { BrushParams, BrushPreset } from "./types";

const base: BrushParams = {
  tip: "round",
  hardness: 0.8,
  grainIntensity: 0,
  strandCount: 0,
  strandCurl: 0,
  fuzz: 0,
  spacing: 0.15,
  scatter: 0,
  rotationJitter: 0,
  opacityJitter: 0,
  followPath: true,
  sizePressure: 0.6,
  opacityPressure: 0.3,
  taper: 0.15,
  streamline: 0.3,
};

const p = (
  id: string,
  name: string,
  set: BrushPreset["set"],
  size: number,
  params: Partial<BrushParams>,
): BrushPreset => ({ id, name, set, size, params: { ...base, ...params } });

export const BRUSH_PRESETS: readonly BrushPreset[] = [
  // ── Sketching ──
  p("pencil", "Pencil", "sketching", 4, {
    tip: "grain",
    grainIntensity: 0.55,
    spacing: 0.08,
    opacityPressure: 0.7,
    sizePressure: 0.35,
    rotationJitter: 1,
    taper: 0.25,
    streamline: 0.35,
  }),
  p("pencil-6b", "6B Pencil", "sketching", 9, {
    tip: "grain",
    grainIntensity: 0.75,
    spacing: 0.08,
    opacityJitter: 0.2,
    opacityPressure: 0.8,
    rotationJitter: 1,
    taper: 0.15,
  }),
  p("charcoal", "Charcoal", "sketching", 14, {
    tip: "grain",
    grainIntensity: 0.95,
    spacing: 0.1,
    scatter: 0.12,
    opacityJitter: 0.35,
    rotationJitter: 1,
    sizePressure: 0.5,
    taper: 0.05,
  }),
  p("hard-pencil", "Hard Pencil (2H)", "sketching", 3, {
    tip: "grain",
    grainIntensity: 0.3,
    hardness: 0.9,
    spacing: 0.06,
    opacityPressure: 0.5,
    sizePressure: 0.2,
    rotationJitter: 1,
    taper: 0.3,
    streamline: 0.45,
  }),
  p("soft-graphite", "Soft Graphite", "sketching", 18, {
    tip: "grain",
    grainIntensity: 0.65,
    hardness: 0.4,
    spacing: 0.09,
    scatter: 0.06,
    opacityJitter: 0.2,
    opacityPressure: 0.85,
    sizePressure: 0.55,
    rotationJitter: 1,
    taper: 0.1,
  }),
  p("conte", "Conté Stick", "sketching", 22, {
    tip: "grain",
    grainIntensity: 0.85,
    hardness: 0.6,
    spacing: 0.11,
    scatter: 0.05,
    opacityJitter: 0.25,
    rotationJitter: 0.1,
    followPath: true,
    sizePressure: 0.25,
    taper: 0,
  }),
  p("colored-pencil", "Colored Pencil", "sketching", 6, {
    tip: "grain",
    grainIntensity: 0.4,
    hardness: 0.75,
    spacing: 0.07,
    opacityPressure: 0.6,
    sizePressure: 0.3,
    rotationJitter: 0.8,
    taper: 0.2,
    streamline: 0.4,
  }),
  p("ink-wash", "Ink Wash", "sketching", 20, {
    tip: "round",
    hardness: 0.35,
    spacing: 0.11,
    scatter: 0.08,
    opacityJitter: 0.2,
    opacityPressure: 0.75,
    sizePressure: 0.4,
    taper: 0.05,
  }),
  // ── Inking ──
  p("studio-pen", "Studio Pen", "inking", 5, {
    tip: "round",
    hardness: 1,
    spacing: 0.04,
    sizePressure: 0.85,
    opacityPressure: 0,
    taper: 0.35,
    streamline: 0.55,
  }),
  p("technical-pen", "Technical Pen", "inking", 3, {
    tip: "round",
    hardness: 1,
    spacing: 0.04,
    sizePressure: 0.1,
    opacityPressure: 0,
    taper: 0.05,
    streamline: 0.65,
  }),
  p("dry-ink", "Dry Ink", "inking", 8, {
    tip: "grain",
    grainIntensity: 0.85,
    hardness: 1,
    spacing: 0.06,
    sizePressure: 0.7,
    opacityPressure: 0.2,
    taper: 0.3,
  }),
  p("gel-pen", "Gel Pen", "inking", 4, {
    tip: "round",
    hardness: 1,
    spacing: 0.03,
    sizePressure: 0.25,
    opacityPressure: 0,
    taper: 0.1,
    streamline: 0.5,
  }),
  p("brush-pen", "Brush Pen", "inking", 12, {
    tip: "round",
    hardness: 0.95,
    spacing: 0.04,
    sizePressure: 1,
    opacityPressure: 0,
    taper: 0.6,
    streamline: 0.6,
  }),
  p("marker", "Marker", "inking", 16, {
    tip: "round",
    hardness: 0.75,
    spacing: 0.05,
    sizePressure: 0.15,
    opacityPressure: 0.15,
    opacityJitter: 0.1,
    taper: 0,
    streamline: 0.4,
  }),
  p("calligraphy", "Calligraphy", "inking", 8, {
    tip: "round",
    hardness: 1,
    spacing: 0.03,
    sizePressure: 0.9,
    opacityPressure: 0,
    rotationJitter: 0.15,
    followPath: true,
    taper: 0.4,
    streamline: 0.7,
  }),
  p("ballpoint", "Ballpoint Pen", "inking", 2, {
    tip: "round",
    hardness: 1,
    spacing: 0.02,
    sizePressure: 0,
    opacityPressure: 0,
    taper: 0,
    streamline: 0.8,
  }),
  p("fountain-pen", "Fountain Pen", "inking", 6, {
    tip: "round",
    hardness: 0.9,
    spacing: 0.03,
    sizePressure: 0.5,
    opacityPressure: 0.1,
    taper: 0.2,
    streamline: 0.6,
  }),
  // ── Painting ──
  p("round-brush", "Round Brush", "painting", 18, {
    tip: "bristle",
    strandCount: 24,
    spacing: 0.09,
    sizePressure: 0.6,
    opacityPressure: 0.4,
    taper: 0.2,
  }),
  p("oil-pastel", "Oil Pastel", "painting", 16, {
    tip: "bristle",
    strandCount: 14,
    grainIntensity: 0.5,
    spacing: 0.07,
    scatter: 0.06,
    opacityJitter: 0.25,
    sizePressure: 0.3,
  }),
  p("chalk", "Chalk", "painting", 20, {
    tip: "grain",
    grainIntensity: 0.9,
    spacing: 0.12,
    scatter: 0.08,
    opacityJitter: 0.3,
    rotationJitter: 1,
    sizePressure: 0.35,
  }),
  p("flat-brush", "Flat Brush", "painting", 24, {
    tip: "bristle",
    strandCount: 40,
    strandCurl: 0,
    spacing: 0.06,
    followPath: true,
    sizePressure: 0.35,
    opacityPressure: 0.25,
    taper: 0.1,
  }),
  p("dry-brush", "Dry Brush", "painting", 22, {
    tip: "bristle",
    strandCount: 18,
    grainIntensity: 0.6,
    fuzz: 0.3,
    spacing: 0.13,
    scatter: 0.1,
    opacityJitter: 0.4,
    sizePressure: 0.45,
    opacityPressure: 0.5,
  }),
  p("wet-wash", "Wet Wash", "painting", 34, {
    tip: "round",
    hardness: 0.2,
    spacing: 0.1,
    scatter: 0.05,
    opacityJitter: 0.15,
    sizePressure: 0.5,
    opacityPressure: 0.6,
    taper: 0.1,
    streamline: 0.5,
  }),
  p("sponge", "Sponge", "painting", 28, {
    tip: "spray",
    hardness: 0.7,
    spacing: 0.3,
    scatter: 0.25,
    opacityJitter: 0.45,
    rotationJitter: 1,
    sizePressure: 0.2,
    taper: 0,
  }),
  p("watercolor", "Watercolor", "painting", 32, {
    tip: "round",
    hardness: 0.15,
    spacing: 0.13,
    scatter: 0.1,
    opacityJitter: 0.35,
    opacityPressure: 0.8,
    sizePressure: 0.45,
    taper: 0.05,
    streamline: 0.4,
  }),
  p("gouache", "Gouache", "painting", 26, {
    tip: "bristle",
    strandCount: 32,
    grainIntensity: 0.35,
    spacing: 0.08,
    scatter: 0.04,
    opacityJitter: 0.1,
    sizePressure: 0.5,
    opacityPressure: 0.3,
    taper: 0.15,
  }),
  p("cloud-soft", "Cloud Soft", "painting", 48, {
    tip: "round",
    hardness: 0.05,
    spacing: 0.15,
    scatter: 0.15,
    opacityJitter: 0.25,
    sizePressure: 0.3,
    opacityPressure: 0.75,
    taper: 0,
  }),
  // ── Materials (fiber reference look) ──
  p("wool", "Wool", "materials", 26, {
    tip: "fiber",
    strandCount: 26,
    strandCurl: 0.55,
    fuzz: 0.7,
    spacing: 0.16,
    scatter: 0.14,
    rotationJitter: 0.6,
    opacityJitter: 0.25,
    sizePressure: 0.4,
    taper: 0.05,
  }),
  p("felt", "Felt", "materials", 30, {
    tip: "fiber",
    strandCount: 40,
    strandCurl: 0.35,
    fuzz: 0.45,
    spacing: 0.12,
    scatter: 0.1,
    opacityJitter: 0.15,
    sizePressure: 0.3,
  }),
  p("boucle", "Bouclé Yarn", "materials", 22, {
    tip: "fiber",
    strandCount: 14,
    strandCurl: 1,
    fuzz: 0.55,
    spacing: 0.22,
    scatter: 0.2,
    rotationJitter: 1,
    opacityJitter: 0.3,
  }),
  p("cloth-weave", "Cloth Weave", "materials", 24, {
    tip: "bristle",
    strandCount: 30,
    grainIntensity: 0.4,
    spacing: 0.14,
    rotationJitter: 0.15,
    opacityJitter: 0.15,
    followPath: true,
  }),
  p("frayed-thread", "Frayed Thread", "materials", 12, {
    tip: "fiber",
    strandCount: 6,
    strandCurl: 0.7,
    fuzz: 1,
    spacing: 0.3,
    scatter: 0.35,
    rotationJitter: 1,
    opacityJitter: 0.4,
  }),
  p("metal-sheen", "Metal", "materials", 22, {
    tip: "bristle",
    strandCount: 48,
    strandCurl: 0.05,
    fuzz: 0.05,
    spacing: 0.08,
    scatter: 0.02,
    opacityJitter: 0.35,
    followPath: true,
    sizePressure: 0.25,
  }),
  p("canvas", "Canvas", "materials", 28, {
    tip: "grain",
    grainIntensity: 0.75,
    spacing: 0.1,
    scatter: 0.06,
    opacityJitter: 0.2,
    sizePressure: 0.4,
    rotationJitter: 0.5,
    taper: 0,
  }),
  p("burlap", "Burlap", "materials", 32, {
    tip: "grain",
    grainIntensity: 0.85,
    spacing: 0.14,
    scatter: 0.12,
    opacityJitter: 0.28,
    sizePressure: 0.35,
    rotationJitter: 1,
    taper: 0,
  }),
  p("leather", "Leather", "materials", 24, {
    tip: "bristle",
    strandCount: 22,
    grainIntensity: 0.6,
    spacing: 0.12,
    scatter: 0.08,
    opacityJitter: 0.2,
    sizePressure: 0.45,
    followPath: true,
  }),
  // ── Airbrush ──
  p("airbrush-soft", "Soft Airbrush", "airbrush", 40, {
    tip: "round",
    hardness: 0.08,
    spacing: 0.12,
    sizePressure: 0.4,
    opacityPressure: 0.7,
    taper: 0,
  }),
  p("airbrush-hard", "Hard Airbrush", "airbrush", 28, {
    tip: "round",
    hardness: 0.55,
    spacing: 0.1,
    sizePressure: 0.5,
    opacityPressure: 0.6,
    taper: 0,
  }),
  p("spray", "Spray", "airbrush", 36, {
    tip: "spray",
    hardness: 0.3,
    spacing: 0.25,
    scatter: 0.15,
    opacityJitter: 0.4,
    rotationJitter: 1,
    taper: 0,
  }),
  p("fine-nozzle", "Fine Nozzle", "airbrush", 14, {
    tip: "spray",
    hardness: 0.6,
    spacing: 0.12,
    scatter: 0.06,
    opacityJitter: 0.25,
    rotationJitter: 1,
    sizePressure: 0.5,
    opacityPressure: 0.7,
    taper: 0,
  }),
  p("splatter", "Splatter", "airbrush", 44, {
    tip: "spray",
    hardness: 0.85,
    spacing: 0.55,
    scatter: 0.6,
    opacityJitter: 0.55,
    rotationJitter: 1,
    sizePressure: 0.6,
    taper: 0,
    streamline: 0.1,
  }),
  p("oil-spray", "Oil Spray", "airbrush", 38, {
    tip: "spray",
    hardness: 0.65,
    spacing: 0.35,
    scatter: 0.45,
    opacityJitter: 0.35,
    rotationJitter: 1,
    sizePressure: 0.55,
    opacityPressure: 0.5,
    taper: 0,
    streamline: 0.15,
  }),
  p("cloud-spray", "Cloud Spray", "airbrush", 50, {
    tip: "round",
    hardness: 0.1,
    spacing: 0.18,
    scatter: 0.2,
    opacityJitter: 0.3,
    sizePressure: 0.35,
    opacityPressure: 0.8,
    taper: 0,
    streamline: 0.2,
  }),
  p("splashy-paint", "Splashy Paint", "airbrush", 42, {
    tip: "spray",
    hardness: 0.4,
    spacing: 0.4,
    scatter: 0.55,
    opacityJitter: 0.5,
    rotationJitter: 1,
    sizePressure: 0.7,
    opacityPressure: 0.6,
    taper: 0,
  }),
];

export const DEFAULT_BRUSH: BrushPreset = BRUSH_PRESETS[0]; // pencil

export const DEFAULT_ERASER_BRUSH: BrushParams = {
  ...base,
  tip: "round",
  hardness: 0.9,
  spacing: 0.08,
  sizePressure: 0.3,
  opacityPressure: 0,
  taper: 0,
};
